import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  BookOpen,
  Rocket,
  Users,
  MessageCircle,
  Files,
  Building2,
  Bot,
  CalendarCheck,
  AlertTriangle,
  Settings,
  Webhook,
  HelpCircle,
  Calculator,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import AppShell from "@/components/layout/AppShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { APP_VERSION, APP_VERSION_DATE, APP_NAME } from "@/lib/version";

/**
 * Centre d'aide complet du CRM SOGI.
 *
 * Le contenu vit dans `SECTIONS` (un tableau structuré). Chaque section a un
 * id (utilisé dans l'URL #fragment), un titre, une icône, et une liste de
 * blocs : "p" (paragraphe), "ul" (puces), "ol" (étapes numérotées), "callout"
 * (note de mise en garde), "kbd" (raccourci clavier), "code" (snippet).
 *
 * Pourquoi pas du Markdown ?
 *   - On veut des liens internes vers les pages CRM (Link react-router) qui
 *     déclenchent la navigation SPA, pas un rechargement.
 *   - On veut des icônes par section + un layout uniforme.
 *   - Pas de dépendance markdown supplémentaire pour <50 KB de doc.
 */

const SECTIONS = [
  // -------------------------------------------------------------------- DEMARRAGE
  {
    id: "demarrage",
    title: "Démarrer en 5 minutes",
    icon: Rocket,
    blocks: [
      { type: "p", text:
        "Le CRM SOGI t'aide à transformer chaque contact WhatsApp en RDV signé. Il automatise tout ce qui peut l'être (welcome, catalogue, relance, prise de RDV) pour que tu te concentres sur les conversations qui comptent." },
      { type: "h", text: "Première fois ici ?" },
      { type: "ol", items: [
        ["Connecte-toi", "Identifie-toi avec ton email + mot de passe (admin@sogi.sn par défaut)."],
        ["Vérifie WhatsApp", "Va dans Paramètres → WhatsApp et scanne le QR code avec ton téléphone (Appareils liés)."],
        ["Active les automatisations", "Dans Paramètres → WhatsApp, active « Message de bienvenue auto » et « Réponse IA aux formulaires »."],
        ["Téléverse ton catalogue", "Paramètres → Documents → Téléverser un document → coche « Envoi automatique »."],
        ["Crée un programme", "Paramètres → Catalogue programmes → Ajouter (nom, ville, lien site web, lien vidéo)."],
        ["Configure la simulation", "Paramètres → Simulation : ajoute tes appartements (typo, surface, prix) et tes modes de paiement (Comptant / Location-Vente)."],
      ]},
      { type: "callout", tone: "info", text:
        "Astuce : tu peux toujours revenir au tableau de bord avec le logo SOGI en haut à gauche." },
    ],
  },

  // -------------------------------------------------------------------- LEADS
  {
    id: "leads",
    title: "Gérer les leads",
    icon: Users,
    blocks: [
      { type: "p", text:
        "Un lead = une fiche prospect. Elle contient ses infos personnelles, son projet immobilier, son historique de conversation et son score de qualité." },
      { type: "h", text: "Créer un lead" },
      { type: "ul", items: [
        ["Manuellement", "Bouton « + Nouveau prospect » dans la barre du haut."],
        ["Par WhatsApp", "Quand quelqu'un t'envoie un formulaire WhatsApp, le lead est créé automatiquement."],
        ["Par formulaire site web", "Avec le webhook public (voir section « Intégration site web »)."],
        ["Import CSV", "Page Leads → bouton « Importer ». Format Meta Ads supporté."],
      ]},
      { type: "h", text: "Statuts du lead" },
      { type: "ul", items: [
        ["Nouveau", "Pas encore contacté."],
        ["Contacté", "Premier message envoyé."],
        ["Intéressé", "A répondu positivement."],
        ["RDV", "Visite ou appel programmé."],
        ["Gagné", "A signé une réservation."],
        ["Perdu", "Plus intéressé ou injoignable."],
      ]},
      { type: "h", text: "Statut conversation (calculé automatiquement)" },
      { type: "p", text:
        "À chaque message reçu, le CRM met à jour un statut basé sur l'historique. Tu vois ce statut sous forme d'un badge coloré sur la fiche lead." },
      { type: "ul", items: [
        ["Catalogue envoyé", "Le catalogue PDF est parti, le lead n'a pas encore répondu."],
        ["RDV proposé", "Tu as proposé un créneau, en attente de confirmation."],
        ["RDV confirmé", "Le lead a accepté un horaire (auto-détecté)."],
        ["RDV décliné", "Le lead a refusé après une proposition."],
        ["Pas de réponse", "Plus de 4h sans retour."],
        ["Erreur formulaire", "Message parasite type « pardon mauvais numéro »."],
      ]},
      { type: "h", text: "Score qualité (0–4 étoiles)" },
      { type: "p", text:
        "Calculé automatiquement à la création : intention d'achat (« maintenant » = 4 étoiles, « je me renseigne » = 0), bonus si type d'appartement précis (+2), bonus si à Dakar (+1)." },
    ],
  },

  // -------------------------------------------------------------------- WHATSAPP
  {
    id: "whatsapp",
    title: "WhatsApp intégré",
    icon: MessageCircle,
    blocks: [
      { type: "p", text:
        "Le CRM se connecte à WhatsApp Web via Baileys. Tous les messages (entrants et sortants) sont archivés dans la fiche du lead." },
      { type: "h", text: "Connecter ton numéro" },
      { type: "ol", items: [
        ["Ouvre WhatsApp Business sur ton téléphone", "Menu → Appareils liés → Lier un appareil."],
        ["Scanne le QR code", "Affiché dans Paramètres → WhatsApp → bouton « Réessayer »."],
        ["Garde le téléphone connecté", "Le téléphone doit rester en ligne pour que le sidecar fonctionne."],
      ]},
      { type: "callout", tone: "warn", text:
        "Recommandation : utilise un numéro WhatsApp Business DÉDIÉ au CRM, jamais ton perso. Risque de bannissement WhatsApp en cas d'envoi massif." },
      { type: "h", text: "Anti-ban automatique" },
      { type: "ul", items: [
        ["Délai aléatoire", "1,5 à 4,5 secondes entre chaque envoi (simule la frappe humaine)."],
        ["Cap quotidien", "200 messages sortants maximum par jour, par numéro."],
        ["Espacement minimum", "2 secondes entre 2 envois consécutifs."],
      ]},
      { type: "h", text: "Voir une conversation" },
      { type: "p", text:
        "Ouvre la fiche d'un lead → onglet Conversation. L'historique complet WhatsApp s'affiche en temps réel." },
      { type: "h", text: "Boutons d'action sur la fiche lead" },
      { type: "ul", items: [
        ["Envoyer catalogue", "Envoie le catalogue PDF du programme préféré du lead."],
        ["Envoyer document", "Choisis n'importe quel document de la bibliothèque."],
        ["Envoyer liens", "Site web + vidéo de présentation du programme."],
        ["SMS", "SMS traditionnel (en dehors de WhatsApp)."],
        ["Appeler", "Compose le numéro depuis ton téléphone."],
      ]},
    ],
  },

  // -------------------------------------------------------------------- DOCUMENTS
  {
    id: "documents",
    title: "Bibliothèque de documents",
    icon: Files,
    blocks: [
      { type: "p", text:
        "Tu peux téléverser autant de documents que tu veux (catalogues, plans, conditions de financement, brochures…). Chaque document peut être envoyé manuellement ou automatiquement à chaque nouveau prospect." },
      { type: "h", text: "Téléverser un document" },
      { type: "ol", items: [
        ["Va dans Paramètres → Documents", "Onglet « Documents » dans la sidebar."],
        ["Clic « Téléverser un document »", "Formats acceptés : PDF, Word, PowerPoint, image. Max 25 MB."],
        ["Renseigne les infos", "Nom affiché, description, message d'accompagnement WhatsApp (optionnel)."],
        ["Active « Envoi automatique »", "Si tu veux qu'il parte à chaque nouveau lead créé."],
      ]},
      { type: "h", text: "Variables dans le message d'accompagnement" },
      { type: "ul", items: [
        ["{prenom}", "Premier mot du nom du lead."],
        ["{nom}", "Nom complet du lead."],
      ]},
      { type: "callout", tone: "info", text:
        "Exemple : « Bonjour {prenom}, voici la documentation comme convenu. Dites-moi ce que vous en pensez. »" },
    ],
  },

  // -------------------------------------------------------------------- PROGRAMMES
  {
    id: "programmes",
    title: "Programmes immobiliers",
    icon: Building2,
    blocks: [
      { type: "p", text:
        "Un programme = un projet immobilier (ex : « RYAD Almadies »). Chaque lead peut être rattaché à un programme. Tu peux y associer un catalogue PDF, un site web et une vidéo." },
      { type: "h", text: "Créer un programme" },
      { type: "ol", items: [
        ["Paramètres → Catalogue programmes", "Bouton « + Ajouter un programme »."],
        ["Renseigne les infos", "Nom, ville, typologie, prix indicatif, statut (actif/épuisé/livré)."],
        ["Ajoute les liens", "URL du site web (ex : ryad-almadies.sn) et de la vidéo de présentation (YouTube/Vimeo)."],
        ["Téléverse le catalogue PDF", "Bouton « Téléverser » sur la ligne du programme."],
      ]},
      { type: "h", text: "Envoyer les liens à un lead" },
      { type: "p", text:
        "Sur la fiche d'un lead → bouton « Envoyer liens » → choisis le programme → coche Site/Vidéo → l'envoi WhatsApp génère automatiquement la prévisualisation (vignette + titre)." },
    ],
  },

  // -------------------------------------------------------------------- AUTOMATISATIONS
  {
    id: "automatisations",
    title: "Automatisations",
    icon: Bot,
    blocks: [
      { type: "p", text:
        "Le CRM peut prendre en charge une grande partie de ton suivi commercial. Toutes les automatisations respectent les heures de silence : 22h–08h Dakar, rien ne s'envoie." },
      { type: "h", text: "1. Message de bienvenue" },
      { type: "p", text:
        "Dès qu'un lead est créé, un message de bienvenue est envoyé automatiquement. Configurable dans Paramètres → WhatsApp." },
      { type: "h", text: "2. Documents auto" },
      { type: "p", text:
        "Tous les documents marqués « Envoi automatique » partent juste après le message de bienvenue, avec délai anti-ban entre chaque." },
      { type: "h", text: "3. Réponse IA Claude (multi-tour)" },
      { type: "p", text:
        "Quand un lead répond, Claude analyse l'historique de la conversation et rédige une réponse adaptée, avec un seul objectif : obtenir un RDV." },
      { type: "ul", items: [
        ["Sénégal (Dakar)", "Propose un RDV physique au showroom."],
        ["Diaspora (hors +221)", "Propose un appel visio (Google Meet / WhatsApp)."],
        ["Sans clé Anthropic", "Bascule sur des réponses fallback déterministes (toujours fonctionnel)."],
      ]},
      { type: "h", text: "4. Détection automatique du RDV" },
      { type: "p", text:
        "Quand le client confirme un horaire (« ok mardi 14h », « parfait jeudi 10h »), le CRM crée automatiquement le RDV : remplit rdv_at, marque la procédure, et programme un rappel pour la veille." },
      { type: "h", text: "5. Relance 4h sans réponse" },
      { type: "p", text:
        "Si le lead ne répond pas dans les 4h après le welcome, une relance part automatiquement (« Avez-vous pu consulter le catalogue ? »). Annulée immédiatement si le lead répond entre temps." },
      { type: "h", text: "6. Rappel veille de RDV" },
      { type: "p", text:
        "La veille du RDV à 10h Dakar, un rappel WhatsApp est envoyé automatiquement (« Petit rappel pour notre rendez-vous demain… »)." },
      { type: "h", text: "7. Détection messages parasites" },
      { type: "p", text:
        "Patterns reconnus : « excusez-moi, mauvais numéro », « j'ai confondu », « wrong number »… Le lead est marqué « erreur formulaire » et sort de la pipeline. Aucune relance n'est envoyée." },
    ],
  },

  // -------------------------------------------------------------------- CALENDRIER
  {
    id: "calendrier",
    title: "Calendrier des RDV",
    icon: CalendarCheck,
    blocks: [
      { type: "p", text:
        "Vue mensuelle qui affiche tous tes RDV, relances et rappels. Les RDV apparaissent en chips vertes avec heure + nom du client." },
      { type: "h", text: "Couleurs et icônes" },
      { type: "ul", items: [
        ["Chip verte", "RDV confirmé (visite ou visio)."],
        ["Bordure jaune + icône ⚠️", "Conflit : 2+ RDV à la même heure le même jour."],
        ["Chip bleue", "Relance à effectuer."],
        ["Chip rouge", "En retard."],
        ["📍 / 🎥", "RDV physique / RDV visio."],
      ]},
      { type: "h", text: "Détail d'un jour" },
      { type: "p", text:
        "Clique sur n'importe quelle case du calendrier → un panneau latéral s'ouvre avec les détails complets (heure, type, programme, téléphone). Un clic sur un RDV ouvre la fiche du lead." },
      { type: "callout", tone: "info", text:
        "Le fuseau horaire affiché est toujours Africa/Dakar, même pour les RDV avec des leads de la diaspora." },
    ],
  },

  // -------------------------------------------------------------------- DASHBOARD
  {
    id: "dashboard",
    title: "Dashboard « À traiter aujourd'hui »",
    icon: AlertTriangle,
    blocks: [
      { type: "p", text:
        "Au lieu de scroller des centaines de leads pour trouver ceux qui ont besoin de toi, le dashboard affiche 4 piles d'actions urgentes." },
      { type: "ul", items: [
        ["RDV dans les 24h", "Tes rendez-vous confirmés du jour et de demain."],
        ["Réponses non lues", "Leads qui ont répondu mais que tu n'as pas encore vus."],
        ["Propositions sans réponse > 48h", "Tes propositions de RDV qui n'ont pas eu de retour."],
        ["Pas de réponse (4h+)", "Leads dans le statut « pas_de_reponse »."],
      ]},
      { type: "p", text:
        "Le dashboard se rafraîchit automatiquement toutes les 60 secondes. Clique sur n'importe quel lead pour ouvrir sa fiche directement." },
    ],
  },

  // -------------------------------------------------------------------- SIMULATION
  {
    id: "simulation",
    title: "Simulation de paiement",
    icon: Calculator,
    blocks: [
      { type: "p", text:
        "L'outil Simulation calcule en direct l'échéancier d'un appartement selon le mode de paiement choisi (Comptant ou Location-Vente), le nombre d'années, l'apport et le taux annuel. Le résultat peut être envoyé instantanément au lead par WhatsApp." },
      { type: "h", text: "Lancer une simulation" },
      { type: "ol", items: [
        ["Ouvre le menu Simulation", "Dans la sidebar (juste après « Rapports »)."],
        ["Filtre par programme", "Si tu as plusieurs projets actifs, choisis celui à simuler."],
        ["Choisis l'appartement", "Sélectionne typo/unité — le prix et la surface sont remplis automatiquement."],
        ["Choisis le mode de paiement", "Comptant (échelonné par étapes) ou Location-Vente (mensualités)."],
        ["Pour Location-Vente : choisis la durée", "3, 5 ou 8 ans (configurable dans Paramètres → Simulation)."],
        ["Ajuste l'apport (optionnel)", "L'apport est de 30 % minimum (politique SOGI). Tu peux monter en % ou en FCFA grâce au toggle."],
        ["Envoie au lead", "Bouton « Envoyer au lead » → choisis le destinataire → le message WhatsApp prérempli part avec tout le détail."],
      ]},
      { type: "h", text: "Comprendre le calcul Location-Vente" },
      { type: "p", text:
        "Le client verse un apport à la signature. Le reste (capital emprunté) est financé à un taux annuel fixe sur la durée choisie. La mensualité est calculée selon la formule d'amortissement standard : M = K × i / (1 − (1+i)^−n) où i = taux annuel ÷ 12 et n = durée en mois." },
      { type: "ul", items: [
        ["Apport", "Montant payé à la signature (par défaut 30 % du prix)."],
        ["Capital emprunté", "Prix − Apport. C'est ce qui est financé au taux annuel."],
        ["Mensualité", "Versement mensuel constant pendant toute la durée."],
        ["Total des mensualités", "Mensualité × nombre de mois."],
        ["Intérêts payés", "Total des mensualités − Capital emprunté."],
        ["Coût total", "Apport + Total des mensualités. C'est ce que le client paye réellement."],
      ]},
      { type: "callout", tone: "info", text:
        "Astuce : la barre verte récapitule en haut Prix de vente · Surface · Mensualité à payer (par mois). Le détail complet (capital, intérêts, coût total) est juste en dessous." },
      { type: "h", text: "Personnaliser l'apport" },
      { type: "p", text:
        "Si le lead souhaite verser plus que les 30 % minimum (cash disponible, héritage…), bascule entre l'input % et l'input FCFA pour saisir le montant exact. La mensualité diminue automatiquement." },
      { type: "h", text: "Configurer les appartements et modes" },
      { type: "ol", items: [
        ["Va dans Paramètres → Simulation", "Onglet dédié."],
        ["Ajoute/édite tes appartements", "Typo, unité, surface, prix, programme. Tu peux aussi cocher les modes interdits par appartement (ex : F2-A sans Location-Vente)."],
        ["Édite les modes de paiement", "Comptant : définis l'échéancier en étapes (Réservation 20 %, Signature 30 %, Livraison 50 %…). Location-Vente : choisis taux annuel + durées disponibles + apport minimum (30 % par défaut)."],
        ["Sauvegarde", "Les changements sont visibles immédiatement dans la page Simulation."],
      ]},
      { type: "callout", tone: "warn", text:
        "L'apport minimum pour Location-Vente est verrouillé à 30 % côté serveur (politique SOGI). Tenter de sauvegarder en dessous est rejeté avec une erreur." },
      { type: "h", text: "Envoyer la simulation au lead" },
      { type: "p", text:
        "Le bouton « Envoyer au lead » ouvre une modale qui te permet de choisir le destinataire (recherche par nom/téléphone/programme) et de prévisualiser/éditer le message WhatsApp avant l'envoi. Le message inclut : prix, mode, apport, capital, taux, durée, mensualité, intérêts, coût total et l'échéancier complet." },
    ],
  },

  // -------------------------------------------------------------------- WEBHOOK
  {
    id: "webhook",
    title: "Intégrer ton site web",
    icon: Webhook,
    blocks: [
      { type: "p", text:
        "Quand quelqu'un remplit un formulaire sur ton site (ryad.sogi.sn par exemple), le lead peut être créé automatiquement dans le CRM via une URL." },
      { type: "h", text: "Endpoint à appeler" },
      { type: "code", text:
        "POST https://api.crm.sogi.sn/api/public/webform-lead?token=TON_SECRET\nContent-Type: application/json\n\n{\n  \"full_name\": \"Mariama Diop\",\n  \"phone_number\": \"+221 77 555 11 22\",\n  \"email\": \"mariama@example.com\",\n  \"type_appartement\": \"f3\",\n  \"ville\": \"Dakar\",\n  \"programme\": \"RYAD Almadies\",\n  \"budget\": \"80M FCFA\"\n}" },
      { type: "h", text: "Configuration" },
      { type: "ol", items: [
        ["Définis le secret partagé", "Variable WEBFORM_SECRET dans backend/.env (ex : chaîne aléatoire de 32 caractères)."],
        ["Donne l'URL au développeur du site", "Avec le token. Wordpress, Webflow et Zapier savent tous appeler ce type d'URL."],
        ["Vérifie", "Soumets un faux formulaire — le lead doit apparaître dans le CRM en 2 secondes."],
      ]},
      { type: "callout", tone: "warn", text:
        "L'endpoint dédoublonne automatiquement : si un lead avec le même numéro existe déjà, le formulaire ne crée pas de doublon." },
    ],
  },

  // -------------------------------------------------------------------- PARAMETRES
  {
    id: "parametres",
    title: "Paramètres en détail",
    icon: Settings,
    blocks: [
      { type: "h", text: "Entreprise" },
      { type: "p", text: "Nom affiché dans le header et les exports CSV." },
      { type: "h", text: "Projets / Campagnes" },
      { type: "p", text:
        "Un projet = une campagne commerciale (ex : « RYAD Pilotage 2026 »). Tu peux avoir plusieurs projets en parallèle. Le projet actif détermine quel programme par défaut est assigné aux nouveaux leads." },
      { type: "h", text: "Catalogue programmes" },
      { type: "p", text: "Liste des programmes immobiliers (voir section dédiée plus haut)." },
      { type: "h", text: "Documents" },
      { type: "p", text: "Bibliothèque de documents partageables (voir section dédiée plus haut)." },
      { type: "h", text: "WhatsApp" },
      { type: "ul", items: [
        ["Connexion WhatsApp", "Scan du QR + statut de la connexion en temps réel."],
        ["Message de bienvenue auto", "Toggle + template personnalisable."],
        ["Réponse IA aux formulaires", "Toggle pour activer Claude sur les messages entrants."],
      ]},
      { type: "h", text: "Automatisations" },
      { type: "p", text:
        "Règles automatisées avancées (en plus des automatisations natives décrites précédemment)." },
    ],
  },

  // -------------------------------------------------------------------- FAQ
  {
    id: "faq",
    title: "FAQ et dépannage",
    icon: HelpCircle,
    blocks: [
      { type: "h", text: "WhatsApp affiche « Service non démarré »" },
      { type: "p", text:
        "Le sidecar WhatsApp est éteint ou crashé. Solution : rafraîchir la page Paramètres → WhatsApp → bouton « Réessayer ». Si persiste, contacter le support technique." },
      { type: "h", text: "Le QR code ne s'affiche pas" },
      { type: "p", text:
        "1) Vérifie que le sidecar tourne (Settings → WhatsApp). 2) Si c'est la première connexion, attends ~10 secondes après le démarrage. 3) Si toujours rien : déconnecte un appareil lié dans WhatsApp Business sur ton téléphone (limite à 4 appareils maximum)." },
      { type: "h", text: "L'IA Claude ne répond pas comme attendu" },
      { type: "p", text:
        "Vérifie que la clé ANTHROPIC_API_KEY est bien renseignée dans backend/.env. Sans clé, c'est le fallback déterministe qui répond (toujours fonctionnel mais moins contextuel)." },
      { type: "h", text: "Un message n'a pas été envoyé" },
      { type: "p", text:
        "Causes possibles : (a) numéro pas sur WhatsApp, (b) heures de silence (22h–08h), (c) cap quotidien atteint (200/jour). Le message est alors différé, pas perdu — il part au prochain créneau autorisé." },
      { type: "h", text: "Comment voir mes RDV à venir ?" },
      { type: "p", text:
        <>Va dans le <Link to="/calendrier" className="underline text-accent-navy">Calendrier</Link>, ou consulte le bloc « RDV dans les 24h » du tableau de bord.</> },
      { type: "h", text: "Comment éviter d'être banni de WhatsApp ?" },
      { type: "ul", items: [
        ["Numéro dédié", "Jamais ton WhatsApp perso pour le CRM."],
        ["Pas de spam", "N'envoie jamais à des contacts qui ne t'ont pas écrit en premier."],
        ["Diversifie les messages", "Évite d'envoyer 50 fois le même texte — l'IA Claude le fait naturellement."],
        ["Respecte le cap", "Le CRM applique 200/jour automatiquement, ne le contourne pas."],
      ]},
      { type: "h", text: "Comment exporter mes leads ?" },
      { type: "p", text:
        <>Page <Link to="/leads" className="underline text-accent-navy">Leads</Link> → bouton « Exporter CSV ». Le fichier inclut toutes les colonnes nécessaires pour réimporter ailleurs.</> },
      { type: "h", text: "Mon « Total » de la simulation est plus élevé que le prix de vente, c'est normal ?" },
      { type: "p", text:
        "Oui, c'est normal en Location-Vente : le client paye un apport puis rembourse le capital emprunté avec un taux d'intérêt annuel. Le « Coût total » = Apport + (Mensualité × nombre de mois) inclut donc les intérêts. Tu peux voir le détail (capital, intérêts, coût total) dans le panneau « Détail du financement »." },
      { type: "h", text: "Puis-je réduire l'apport en dessous de 30 % ?" },
      { type: "p", text:
        "Non, l'apport minimum pour la Location-Vente est verrouillé à 30 % côté serveur (politique SOGI). Tu peux uniquement augmenter l'apport — saisi en pourcentage ou en montant FCFA selon ce qui est le plus pratique pour ton lead." },
      { type: "h", text: "Comment changer le taux d'intérêt ou les durées proposées ?" },
      { type: "p", text:
        <>Va dans <Link to="/parametres" className="underline text-accent-navy">Paramètres → Simulation</Link>. Tu peux y éditer le taux annuel, les durées disponibles (3/5/8 ans par défaut) et l'apport minimum pour chaque mode de paiement.</> },
    ],
  },
];

/* ================================================================== */

const TONE_CLASSES = {
  info: "border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-200",
  warn: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200",
};

function Block({ block }) {
  switch (block.type) {
    case "h":
      return (
        <h3 className="font-heading mt-6 mb-2 text-base font-bold tracking-tight">
          {block.text}
        </h3>
      );
    case "p":
      return (
        <p className="mb-3 text-sm leading-relaxed text-foreground/85">
          {block.text}
        </p>
      );
    case "ul":
      return (
        <ul className="mb-3 space-y-1.5 text-sm">
          {block.items.map(([title, desc], i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-1.5 h-1 w-1 flex-none rounded-full bg-accent-navy dark:bg-accent-gold" />
              <span>
                <b>{title}</b>
                {desc ? <> — <span className="text-muted-foreground">{desc}</span></> : null}
              </span>
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol className="mb-3 space-y-2 text-sm">
          {block.items.map(([title, desc], i) => (
            <li key={i} className="flex gap-3">
              <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-accent-navy text-[11px] font-bold text-white dark:bg-accent-gold dark:text-zinc-900">
                {i + 1}
              </span>
              <span>
                <b>{title}</b>
                {desc ? <> — <span className="text-muted-foreground">{desc}</span></> : null}
              </span>
            </li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <div className={cn("mb-3 rounded-lg border px-3 py-2 text-xs", TONE_CLASSES[block.tone] || TONE_CLASSES.info)}>
          💡 {block.text}
        </div>
      );
    case "code":
      return (
        <pre className="mb-3 overflow-x-auto rounded-lg border border-border/60 bg-zinc-950 p-3 text-[11px] leading-relaxed text-zinc-100">
          <code>{block.text}</code>
        </pre>
      );
    default:
      return null;
  }
}

/* ================================================================== */

function searchableText(section) {
  const flat = [section.title];
  for (const b of section.blocks) {
    if (typeof b.text === "string") flat.push(b.text);
    if (b.items) {
      for (const item of b.items) {
        flat.push(...item.filter((x) => typeof x === "string"));
      }
    }
  }
  return flat.join(" ").toLowerCase();
}

export default function HelpCenterPage() {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState(null);

  // Pre-compute searchable index once
  const index = useMemo(
    () => SECTIONS.map((s) => ({ id: s.id, hay: searchableText(s) })),
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    const matchingIds = new Set(
      index.filter((s) => s.hay.includes(q)).map((s) => s.id)
    );
    return SECTIONS.filter((s) => matchingIds.has(s.id));
  }, [query, index]);

  // Highlight section in the TOC as the user scrolls
  useEffect(() => {
    const sectionEls = SECTIONS.map((s) => document.getElementById(`help-${s.id}`)).filter(Boolean);
    if (!sectionEls.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) {
          setActiveId(visible.target.id.replace(/^help-/, ""));
        }
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.25, 0.5] }
    );
    sectionEls.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [filtered.length]);

  // Anchor jump on first load if URL has a hash
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (hash) {
      const el = document.getElementById(`help-${hash}`);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, []);

  const jump = (id) => {
    const el = document.getElementById(`help-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", `#${id}`);
    }
  };

  return (
    <AppShell topbarProps={{ showSearch: false, showAdd: false }}>
      <div className="mx-auto max-w-[1400px] px-4 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-label text-muted-foreground">Documentation</p>
            <span
              className="rounded-full bg-accent-navy/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-accent-navy dark:bg-accent-gold/20 dark:text-accent-gold"
              data-testid="help-version-badge"
            >
              {APP_NAME} v{APP_VERSION}
            </span>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
            Centre d'aide
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Tout ce qu'il faut savoir pour bien utiliser le CRM SOGI au quotidien.
          </p>
        </div>

        {/* Search bar */}
        <div className="mb-6 flex items-center gap-2 rounded-xl border border-border/60 bg-card px-3 py-2 shadow-soft">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher une fonctionnalité (RDV, WhatsApp, document…)"
            className="border-0 bg-transparent px-0 shadow-none focus-visible:ring-0"
            data-testid="help-search"
          />
          {query && (
            <Button variant="ghost" size="sm" onClick={() => setQuery("")} className="h-7">
              Effacer
            </Button>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
          {/* Sidebar: table of contents */}
          <nav className="self-start lg:sticky lg:top-20" aria-label="Sommaire">
            <div className="rounded-xl border border-border/60 bg-card p-3 shadow-soft">
              <div className="mb-2 flex items-center gap-2 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <BookOpen className="h-3.5 w-3.5" />
                Sommaire
              </div>
              <div className="space-y-0.5">
                {filtered.map((s) => {
                  const Icon = s.icon;
                  const active = s.id === activeId;
                  return (
                    <button
                      key={s.id}
                      onClick={() => jump(s.id)}
                      className={cn(
                        "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition",
                        active
                          ? "bg-accent-navy text-white dark:bg-primary dark:text-primary-foreground"
                          : "hover:bg-muted/60"
                      )}
                      data-testid={`help-toc-${s.id}`}
                    >
                      <Icon className={cn("h-3.5 w-3.5 flex-none", active ? "" : "text-muted-foreground")} />
                      <span className="truncate">{s.title}</span>
                      {!active && <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground opacity-0 transition group-hover:opacity-100" />}
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <p className="px-2 py-3 text-xs text-muted-foreground">
                    Aucun résultat. Essaie un autre mot-clé.
                  </p>
                )}
              </div>
            </div>

            {/* Quick links */}
            <div className="mt-4 rounded-xl border border-border/60 bg-card p-3 shadow-soft">
              <div className="mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Liens rapides
              </div>
              <div className="space-y-1">
                <Link to="/parametres" className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60">
                  <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                  Paramètres
                  <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                </Link>
                <Link to="/calendrier" className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60">
                  <CalendarCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  Calendrier
                  <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                </Link>
                <Link to="/simulation" className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60">
                  <Calculator className="h-3.5 w-3.5 text-muted-foreground" />
                  Simulation
                  <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                </Link>
                <Link to="/leads" className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/60">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  Mes leads
                  <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground" />
                </Link>
              </div>
            </div>
          </nav>

          {/* Main content */}
          <main className="min-w-0">
            {filtered.map((section) => {
              const Icon = section.icon;
              return (
                <section
                  key={section.id}
                  id={`help-${section.id}`}
                  className="mb-6 scroll-mt-24 rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6"
                  data-testid={`help-section-${section.id}`}
                >
                  <header className="mb-4 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-navy/10 text-accent-navy dark:bg-accent-gold/20 dark:text-accent-gold">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h2 className="font-heading text-xl font-bold tracking-tight">
                      {section.title}
                    </h2>
                  </header>
                  <div>
                    {section.blocks.map((block, i) => (
                      <Block key={i} block={block} />
                    ))}
                  </div>
                </section>
              );
            })}

            {filtered.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border/60 bg-card p-12 text-center shadow-soft">
                <HelpCircle className="mx-auto h-10 w-10 text-muted-foreground/40" />
                <h3 className="mt-3 font-heading text-base font-bold">Aucun résultat</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Essaie d'autres mots-clés, ou efface la recherche pour voir tout le guide.
                </p>
              </div>
            )}

            <footer className="mt-8 rounded-xl border border-border/60 bg-muted/30 p-4 text-center text-xs text-muted-foreground">
              <div>Une question pas couverte ici ? Contacte l'équipe SOGI.</div>
              <div className="mt-2 text-[10px]">
                {APP_NAME} <b>v{APP_VERSION}</b> · Build du {APP_VERSION_DATE}
              </div>
            </footer>
          </main>
        </div>
      </div>
    </AppShell>
  );
}
